import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { fehlerText, planerApi } from './api';

/* Programme mit Phasen (W3.1): Phasen mit Dauer in Wochen, Plan je Phase aus einer eigenen Vorlage, Deload als eigene
   Phase (ohne eigenen Plan = Plan der Phase davor), optional eine Ernährungsplan-Vorlage. Zugewiesen wird in der
   Personen-Karte; der Wechsel in die nächste Phase läuft automatisch mit einer Push an das Mitglied. */

const leerePhase = () => ({ titel: '', wochen: 4, deload: false, plan: '' });

function Formular({ start, vorlagen, ernaehrung, onSpeichern, onAbbrechen, busy }) {
  const [f, setF] = useState(start);
  const setPhase = (i, w) => setF({ ...f, phasen: f.phasen.map((p, j) => (j === i ? { ...p, ...w } : p)) });
  const tausche = (i, j) => { const ph = [...f.phasen]; [ph[i], ph[j]] = [ph[j], ph[i]]; setF({ ...f, phasen: ph }); };
  return (
    <div className="space-y-3 rounded-[14px] border border-border bg-card p-4" data-testid="programm-formular">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-xs"><span className="text-muted-foreground">Titel</span>
          <Input value={f.titel} maxLength={120} onChange={(e) => setF({ ...f, titel: e.target.value })} className="h-11" /></label>
        <label className="flex flex-col gap-1 text-xs"><span className="text-muted-foreground">Beschreibung</span>
          <Input value={f.beschreibung || ''} maxLength={500} onChange={(e) => setF({ ...f, beschreibung: e.target.value })} className="h-11" /></label>
      </div>
      <ol className="space-y-2">
        {f.phasen.map((p, i) => (
          <li key={i} className="grid items-end gap-2 rounded-md border border-border p-2 sm:grid-cols-[2rem_1fr_6rem_8rem_1fr_1fr_auto]">
            <span className="text-sm font-bold">{i + 1}.</span>
            <label className="flex flex-col gap-0.5 text-xs"><span className="text-muted-foreground">Phase</span>
              <Input value={p.titel} maxLength={80} onChange={(e) => setPhase(i, { titel: e.target.value })} className="h-11" placeholder="z. B. Grundlage" /></label>
            <label className="flex flex-col gap-0.5 text-xs"><span className="text-muted-foreground">Wochen</span>
              <Input type="number" min={1} max={12} value={p.wochen} onChange={(e) => setPhase(i, { wochen: Number(e.target.value) })} className="h-11" /></label>
            <label className="flex min-h-[44px] items-center gap-1.5 text-sm"><input type="checkbox" className="h-5 w-5 accent-marke" checked={p.deload} onChange={(e) => setPhase(i, { deload: e.target.checked })} /> Deload</label>
            <label className="flex flex-col gap-0.5 text-xs"><span className="text-muted-foreground">Plan</span>
              <select aria-label={`Plan der Phase ${i + 1}`} value={p.plan} onChange={(e) => setPhase(i, { plan: e.target.value })}
                className="h-11 rounded-md border border-input bg-background px-2 text-sm">
                <option value="">{p.deload && i > 0 ? 'wie Phase davor' : 'Plan wählen …'}</option>
                {p.alt && <option value={`alt:${p.alt}`}>bisheriger Plan behalten</option>}
                {vorlagen.map((v) => <option key={v.id} value={`v:${v.id}`}>{v.name}</option>)}
              </select></label>
            <label className="flex flex-col gap-0.5 text-xs"><span className="text-muted-foreground">Ernährungsplan (optional)</span>
              <select aria-label={`Ernährungsplan der Phase ${i + 1}`} value={p.ernaehrungsplan_vorlage_id || ''} disabled={!ernaehrung.length}
                onChange={(e) => setPhase(i, { ernaehrungsplan_vorlage_id: e.target.value || null })} className="h-11 rounded-md border border-input bg-background px-2 text-sm">
                <option value="">keiner</option>
                {ernaehrung.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select></label>
            <div className="flex gap-1">
              <Button variant="ghost" size="icon" className="h-11 w-11" aria-label="Phase nach oben" disabled={i === 0} onClick={() => tausche(i, i - 1)}><ArrowUp className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" className="h-11 w-11" aria-label="Phase nach unten" disabled={i === f.phasen.length - 1} onClick={() => tausche(i, i + 1)}><ArrowDown className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" className="h-11 w-11" aria-label="Phase löschen" disabled={f.phasen.length === 1}
                onClick={() => setF({ ...f, phasen: f.phasen.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" className="min-h-[44px] gap-1" onClick={() => setF({ ...f, phasen: [...f.phasen, leerePhase()] })}><Plus className="h-4 w-4" /> Phase</Button>
        <Button variant="outline" className="min-h-[44px] gap-1"
          onClick={() => setF({ ...f, phasen: [...f.phasen, { titel: 'Deload', wochen: 1, deload: true, plan: '' }] })}><Plus className="h-4 w-4" /> Deload-Phase</Button>
        <span className="flex-1" />
        <Button variant="ghost" className="min-h-[44px]" onClick={onAbbrechen}>Abbrechen</Button>
        <Button className="min-h-[44px]" disabled={busy || f.titel.trim().length < 2} onClick={() => onSpeichern(f)}>Speichern</Button>
      </div>
      <p className="text-xs text-muted-foreground">Gespeicherte Vorlagen entstehen im Plan-Editor über „Als Vorlage“. Eine laufende Zuweisung behält ihre Phasen, auch wenn das Programm später geändert wird.</p>
    </div>
  );
}

function phasenFuerServer(phasen) {
  return phasen.map((p) => {
    const out = { titel: p.titel.trim(), wochen: p.wochen, deload: p.deload, ernaehrungsplan_vorlage_id: p.ernaehrungsplan_vorlage_id || null };
    if (p.plan.startsWith('v:')) out.vorlage_id = p.plan.slice(2);
    if (p.plan.startsWith('alt:')) out.plan_von_phase = Number(p.plan.slice(4));
    return out;
  });
}

export default function Programme({ client, basis, ernaehrungVorlagenLaden }) {
  const api = useMemo(() => planerApi(client, basis), [client, basis]);
  const [liste, setListe] = useState(null);
  const [darf, setDarf] = useState(false);
  const [vorlagen, setVorlagen] = useState([]);
  const [ernaehrung, setErnaehrung] = useState([]);
  const [form, setForm] = useState(null);       // {id?, version?, titel, beschreibung, phasen}
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const lade = useCallback(() => {
    api.programme().then((r) => { setListe(r.data.programme); setDarf(r.data.darf_pflegen); setErr(''); })
      .catch((e) => setErr(fehlerText(e, 'Programme nicht geladen')));
    api.vorlagen().then((r) => setVorlagen(r.data.eigene || [])).catch(() => setVorlagen([]));
  }, [api]);
  useEffect(() => { lade(); }, [lade]);
  useEffect(() => {
    if (!ernaehrungVorlagenLaden) return;
    ernaehrungVorlagenLaden().then((r) => setErnaehrung(r.data?.vorlagen || r.data || [])).catch(() => setErnaehrung([]));
  }, [ernaehrungVorlagenLaden]);

  const speichern = async (f) => {
    setBusy(true);
    const body = { titel: f.titel.trim(), beschreibung: f.beschreibung || '', phasen: phasenFuerServer(f.phasen) };
    try {
      if (f.id) await api.programmAendern(f.id, { ...body, version: f.version });
      else await api.programmNeu(body);
      toast.success('Programm gespeichert'); setForm(null); lade();
    } catch (e) { toast.error(fehlerText(e, 'Nicht gespeichert')); if (e?.response?.status === 409) lade(); }
    setBusy(false);
  };
  const archivieren = async (p) => {
    try { await api.programmAendern(p.id, { version: p.version, archiviert: true }); toast.success('Archiviert'); lade(); }
    catch (e) { toast.error(fehlerText(e, 'Nicht archiviert')); }
  };

  if (err) return <p className="text-sm text-destructive" role="alert">{err}</p>;
  if (!liste) return <Skeleton className="h-32" />;
  return (
    <div className="space-y-3" data-testid="programme">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-base font-extrabold">Programme mit Phasen</p>
          <p className="text-xs text-muted-foreground">Zuweisen in der Karte „Trainingspläne“ einer Person · Phasenwechsel automatisch mit Push</p>
        </div>
        {darf && !form && <Button className="min-h-[44px] gap-1.5" onClick={() => setForm({ titel: '', beschreibung: '', phasen: [leerePhase()] })}><Plus className="h-4 w-4" /> Programm</Button>}
      </div>
      {form && <Formular start={form} vorlagen={vorlagen} ernaehrung={ernaehrung} busy={busy} onSpeichern={speichern} onAbbrechen={() => setForm(null)} />}
      {!liste.length && !form && <p className="text-sm text-muted-foreground">Noch kein Programm.</p>}
      <ul className="grid gap-3 sm:grid-cols-2">
        {liste.map((p) => (
          <li key={p.id} className="rounded-[14px] border border-border bg-card p-4">
            <p className="font-extrabold">{p.titel}</p>
            <p className="text-xs text-muted-foreground">{p.wochen} Wochen · {p.phasen.length} Phasen{p.erstellt_von_name ? ` · ${p.erstellt_von_name}` : ''}</p>
            <ol className="mt-2 flex flex-wrap gap-1">
              {p.phasen.map((ph) => <li key={ph.nr} className="rounded-full border border-border bg-muted px-[11px] py-[5px] text-xs font-bold">{ph.titel} · {ph.wochen} W{ph.deload ? ' · Deload' : ''}</li>)}
            </ol>
            {darf && (
              <div className="mt-2 flex gap-2">
                <Button size="sm" variant="outline" className="min-h-[44px]" onClick={() => setForm({ id: p.id, version: p.version, titel: p.titel, beschreibung: p.beschreibung,
                  phasen: p.phasen.map((ph) => ({ titel: ph.titel, wochen: ph.wochen, deload: ph.deload, ernaehrungsplan_vorlage_id: ph.ernaehrungsplan_vorlage_id,
                    alt: ph.nr, plan: ph.vorlage_id ? `v:${ph.vorlage_id}` : ph.eigener_plan ? `alt:${ph.nr}` : '' })) })}>Bearbeiten</Button>
                <Button size="sm" variant="ghost" className="min-h-[44px]" onClick={() => archivieren(p)}>Archivieren</Button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
